import React, { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../supabaseClient'
import { useCompany } from '../CompanyContext'
import { useImpersonation } from '../ImpersonationContext'
import {
  perfilParaMotor,
  generateChecklist,
  scoreChecklist,
  getCriticalGaps,
  type ComplianceChecklist,
  type CompanyProfileForEngine,
  type ChecklistItem,
  type CategoriaGT,
} from '../lib/compliance-engine'
import { syncIncidentCompliance } from '../lib/incident-compliance-sync'
import { syncGa02Compliance } from '../lib/ga02-compliance-sync'
import { syncWorkPermitCompliance } from '../lib/workpermit-compliance-sync'
import { syncInspectionCompliance } from '../lib/inspection-compliance-sync'
import { syncProductsCompliance } from '../lib/products-compliance-sync'
import { syncJobPositionsCompliance } from '../lib/job-positions-compliance-sync'
import { syncIperCompliance } from '../lib/iper-compliance-sync'
import { aplicarCalculoPlanV2, syncPlanEmergenciasCompliance, TrazasPlanV2 } from '../lib/plan-emergencias-compliance-sync'
import { aplicarCalculoInspecciones, FUENTE_INSPECCIONES_V2, syncInspeccionesV2Compliance } from '../lib/inspecciones/insp-checklist'
import { aplicarCalculoAnexo1, FUENTE_INSPECCIONES_ANEXO1, syncInspeccionesAnexo1, tieneQuimicosOBodegas, trazaGT9ModuloAnterior,
  type EstadoCalculoAnexo1 } from '../lib/inspecciones/insp-anexo1'
import { aplicarCalculoFirmas, FUENTE_FIRMAS, syncFirmasCompliance } from '../lib/firmas/firmas-checklist'
import { aplicarCalculoProgramas, FUENTE_PROGRAMAS, syncProgramasCompliance } from '../lib/programas/prog-checklist'
import { conteoActivosChecklist } from '../lib/mantenimiento/mant-datos'
import { aplicarCalculoIncidentes, FUENTE_INCIDENTES_V2, type TrazaItemIncidentes } from '../lib/incidentes/inc-checklist'
import { aplicarCalculoInformes, FUENTE_INFORMES_PUESTO } from '../lib/informes-puesto/ip-checklist'
import { informesMandanGT9, syncInformesPuesto } from '../lib/informes-puesto/ip-datos'
import type { EstadoCalculoInc } from '../lib/incidentes/inc-estado'
import { syncLoteG } from '../lib/inspecciones/insp-lote-g'
import { puestosOperanVehiculos, syncLicenciasCompliance } from '../lib/licencias-conduccion'
import { aplicarCalculoEppPO21, FUENTE_EPP_PO21, syncEppPO21 } from '../lib/epp/epp-po21'
import { type InformeGA07 } from '../lib/ga07-meses'
import {
  ITEMS_AUTOMATICOS, REGLA_COMUN_AUTOMATICOS, esAutomatico, muestraBotonNoAplica, puedeMarcarNoAplica,
  puedeEditarChecklist, sincronizaChecklist,
  validarJustificacionNoAplica, textoNoAplicaManual, MIN_JUSTIFICACION_NO_APLICA,
} from '../lib/checklist-reglas'
import { logAudit } from '../auditLog'
import MesesGA07 from '../components/MesesGA07'
import AniosGA11 from '../components/AniosGA11'
import { abrirArchivo } from '../lib/archivos'
import { abrePaginaDelItem, destinoResolver, type NavItem } from '../lib/menu'
import { preguntasSinResponder, textoPreguntasSinResponder } from '../lib/servicios-permanentes'
import './ChecklistPage.css'

// Las 5 secciones del Anexo 1 en su orden oficial, más un bloque aparte para las
// obligaciones que no son ítems del Anexo (GT-15/16/17, PO-28/29).
const SECCION_LABELS: Record<string, string> = {
  gestion_administrativa: 'Gestión administrativa',
  gestion_tecnica: 'Gestión técnica',
  talento_humano: 'Gestión del talento humano',
  procedimientos_operativos: 'Procedimientos operativos básicos',
  servicios_permanentes: 'Servicios permanentes',
  complementarias: 'Obligaciones complementarias',
}

// Abreviatura de cada sección tal como la usa el Anexo 1 (ej. "GT 15").
const SECCION_ABREV: Record<string, string> = {
  gestion_administrativa: 'GA',
  gestion_tecnica: 'GT',
  talento_humano: 'GTH',
  procedimientos_operativos: 'POB',
  servicios_permanentes: 'SP',
}

const CATEGORIA_GT_LABELS: Record<CategoriaGT, string> = {
  identificacion: 'Identificación',
  higiene: 'Higiene industrial',
  ergonomia_psicosocial: 'Ergonomía y psicosocial',
  medidas: 'Medidas de prevención',
  condiciones: 'Condiciones de trabajo',
  senalizacion: 'Señalización',
  trabajos_especiales: 'Trabajos especiales',
}

function grupoDeItem(item: ChecklistItem): string {
  return item.complementario ? 'complementarias' : item.seccion_anexo
}

// "GT 15" para ítems del Anexo; el id interno para los complementarios.
function etiquetaLegal(item: ChecklistItem): string {
  return item.numero_legal != null ? `${SECCION_ABREV[item.seccion_anexo]} ${item.numero_legal}` : item.id
}

// Orden de presentación: por número legal; los complementarios (sin número) por id interno.
function compararPorNumeroLegal(a: ChecklistItem, b: ChecklistItem): number {
  if (a.numero_legal != null && b.numero_legal != null) return a.numero_legal - b.numero_legal
  if (a.numero_legal != null) return -1
  if (b.numero_legal != null) return 1
  return a.id.localeCompare(b.id)
}

// Módulos StorSafe que ya tienen navegación cruzada implementada desde el checklist.
// GA-02 va a Documentos SSO como los demás (el generador del RHS se quitó, bloque 5).
function moduloTieneNavegacion(itemId: string, modulo?: string): boolean {
  return modulo === 'Documentos SSO' || modulo === 'Perfil SSO' || modulo === 'Vigilancia Salud'
    || modulo === 'Permisos de Trabajo' || modulo === 'Productos' || modulo === 'Etiquetado SGA' || modulo === 'Inspecciones'
    || modulo === 'Puestos de Trabajo'
    || modulo === 'Matriz de Riesgos'
    || modulo === 'Trabajadores' || modulo === 'Capacitaciones' || modulo === 'Incidentes'
    || modulo === 'Plan de Emergencias' || modulo === 'EPP' || modulo === 'Riesgo Psicosocial'
    || modulo === 'Constructor Gestión Técnica'
    // Lote A: los ítems que calculan las inspecciones firmadas (destino en NAV_POR_MODULO de menu.ts).
    || modulo === 'Inspecciones v2 (Productos químicos)' || modulo === 'Inspecciones v2 (Condiciones generales)'
}

// Ítems que escribe un sync (bloque 6, D): ITEMS_AUTOMATICOS en checklist-reglas.ts.
// Manda el módulo: los botones manuales quedan bloqueados siempre.

const ESTADO_CONFIG = {
  pendiente:   { label: 'Pendiente',   cls: 'cl-estado-pendiente' },
  en_proceso:  { label: 'En proceso',  cls: 'cl-estado-proceso' },
  cumple:      { label: 'Cumple',      cls: 'cl-estado-cumple' },
  no_cumple:   { label: 'No cumple',   cls: 'cl-estado-nocumple' },
} as const

type EstadoKey = keyof typeof ESTADO_CONFIG
// Etiqueta del estado que calcula un módulo (Incidentes también puede calcular "No aplica").
const etiquetaCalculo = (e: string) => (e === 'no_aplica' ? 'No aplica' : ESTADO_CONFIG[e as EstadoKey]?.label ?? e)
// 'no_aplica' = "No aplica" manual del administrador (bloque 6, C; M15).
type EstadoGuardado = EstadoKey | 'no_aplica'

interface ItemEstado {
  [itemId: string]: EstadoGuardado
}

interface NoAplicaInfo {
  justificacion: string
  por: string | null
  at: string | null
}

const FILTROS = ['todos', 'aplica', 'pendiente', 'en_proceso', 'cumple', 'no_cumple', 'no_aplica'] as const

function coincideFiltro(item: ChecklistItem, filtro: string): boolean {
  if (filtro === 'todos') return true
  if (filtro === 'aplica') return item.aplica && item.estado !== 'no_aplica'
  if (filtro === 'no_aplica') return !item.aplica || item.estado === 'no_aplica'
  return item.aplica && item.estado === filtro
}

// Primer ítem que muestra un filtro, en el orden de la pantalla: su sección es
// la que se abre al entrar con ese filtro (bloque 5d, F).
function primerItemDelFiltro(items: ChecklistItem[], filtro: string): ChecklistItem | undefined {
  for (const seccion of Object.keys(SECCION_LABELS)) {
    const primero = items.filter(i => grupoDeItem(i) === seccion && coincideFiltro(i, filtro)).sort(compararPorNumeroLegal)[0]
    if (primero) return primero
  }
  return undefined
}

// Nombres de quienes marcaron "No aplica" (bloque 6, C5). Sin permiso o sin
// perfil, el texto dice "usuario sin nombre".
async function leerNombres(ids: (string | null)[]): Promise<Record<string, string>> {
  const unicos = Array.from(new Set(ids.filter((x): x is string => !!x)))
  if (unicos.length === 0) return {}
  const { data, error } = await supabase.from('profiles').select('id, full_name, email').in('id', unicos)
  if (error) { console.error('No se pudieron leer los nombres de quienes marcaron No aplica:', error.message); return {} }
  return Object.fromEntries((data ?? []).map((p: { id: string; full_name: string | null; email: string | null }) => [p.id, p.full_name || p.email || '']))
}

// Entrada al Checklist desde otra pantalla (bloque 5d, F), mismo patrón que
// ss_init_tab: se lee una vez al montar y se borra. Con un ítem se abre solo su
// sección y se lleva la vista a él; con un filtro, la sección del primer ítem que muestra.
const ENTRADA_ITEM = 'ss_init_checklist_abrir_item'
const ENTRADA_FILTRO = 'ss_init_checklist_filtro'

type DocumentoVinculado = InformeGA07

interface ChecklistPageProps {
  onNavigateToDocumentosSSO?: () => void
  onNavigateToSSOPerfil?: () => void
  onNavigateToVigilanciaSalud?: () => void
  onNavigateToPermisos?: () => void
  onNavigateToProductos?: () => void
  onNavigateToEtiquetadoSGA?: () => void
  onNavigateToInspecciones?: () => void
  onNavigateToInspeccionesV2?: () => void
  onNavigateToPuestos?: () => void
  onNavigateToMatrizRiesgos?: () => void
  onNavigateToTrabajadores?: () => void
  onNavigateToCapacitaciones?: () => void
  onNavigateToIncidentes?: () => void
  onNavigateToPlanEmergencias?: () => void
  onNavigateToEPP?: () => void
  onNavigateToRiesgoPsicosocial?: () => void
  onNavigateToConstructorGT?: () => void
  /** Página común del ítem (bloque 7, B4 y C4): las flechas de GA y SP llevan aquí. */
  onNavigateToItemAnexo?: (itemId: string) => void
}

export default function ChecklistPage({
  onNavigateToDocumentosSSO,
  onNavigateToSSOPerfil,
  onNavigateToVigilanciaSalud,
  onNavigateToPermisos,
  onNavigateToProductos,
  onNavigateToEtiquetadoSGA,
  onNavigateToInspecciones,
  onNavigateToInspeccionesV2,
  onNavigateToPuestos,
  onNavigateToMatrizRiesgos,
  onNavigateToTrabajadores,
  onNavigateToCapacitaciones,
  onNavigateToIncidentes,
  onNavigateToPlanEmergencias,
  onNavigateToEPP,
  onNavigateToRiesgoPsicosocial,
  onNavigateToConstructorGT,
  onNavigateToItemAnexo,
}: ChecklistPageProps) {
  const { company, profile: perfilUsuario } = useCompany()
  const { impersonated } = useImpersonation()
  const effectiveCompanyId = (impersonated ?? company)?.id ?? ''
  // C2: "No aplica" manual solo el administrador de la empresa o el superadmin.
  const puedeNoAplica = puedeMarcarNoAplica(perfilUsuario?.role, !!impersonated)
  // Bloque 9 (M19): el viewer solo mira; no cambia estados ni dispara los syncs.
  const puedeEditar = puedeEditarChecklist(perfilUsuario?.role, !!impersonated)
  const sincroniza = sincronizaChecklist(perfilUsuario?.role)
  const [noAplicaInfo, setNoAplicaInfo] = useState<Record<string, NoAplicaInfo>>({})
  const [nombres, setNombres] = useState<Record<string, string>>({})
  const [noAplicaAbierto, setNoAplicaAbierto] = useState<string | null>(null)
  const [justificacion, setJustificacion] = useState('')
  const [errorJustificacion, setErrorJustificacion] = useState('')
  const [checklist, setChecklist]     = useState<ComplianceChecklist | null>(null)
  const [estados, setEstados]         = useState<ItemEstado>({})
  const [loading, setLoading]         = useState(true)
  const [saving, setSaving]           = useState<string | null>(null)
  const [error, setError]             = useState('')
  const [filtro, setFiltro]           = useState<string>('todos')
  const [seccionAbierta, setSeccionAbierta] = useState<string | null>(null)
  const [score, setScore]             = useState(0)
  const [documentMap, setDocumentMap] = useState<Map<string, DocumentoVinculado[]>>(new Map())
  // Error al guardar un estado (E2): se muestra arriba sin reemplazar la página.
  const [errorEstado, setErrorEstado] = useState('')
  const [itemDestacado, setItemDestacado] = useState<string | null>(null)
  // PE-B2: lo que calcula el Plan de Emergencias v2 para PO-17 (no bloquea los botones: se respeta lo manual).
  const [trazasPlan, setTrazasPlan] = useState<TrazasPlanV2>({})
  const entrada = useRef<{ item: string | null; filtro: string | null } | null>(null)

  // Va antes del efecto que carga el checklist: la entrada ya está leída cuando termina la carga.
  useEffect(() => {
    const item = sessionStorage.getItem(ENTRADA_ITEM)
    const filtroEntrada = sessionStorage.getItem(ENTRADA_FILTRO)
    sessionStorage.removeItem(ENTRADA_ITEM)
    sessionStorage.removeItem(ENTRADA_FILTRO)
    if (item || filtroEntrada) entrada.current = { item, filtro: filtroEntrada }
  }, [])

  // Lleva la vista al ítem destacado (entrada con ítem o clic en una brecha crítica).
  useEffect(() => {
    if (!itemDestacado) return
    document.getElementById(`cl-item-${itemDestacado}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
    const t = setTimeout(() => setItemDestacado(null), 2500)
    return () => clearTimeout(t)
  }, [itemDestacado, seccionAbierta])

  const loadChecklist = useCallback(async () => {
    if (!effectiveCompanyId) return
    setLoading(true)
    setError('')
    try {
      const { data: ssoProfile, error: e1 } = await supabase
        .from('company_sso_profile')
        .select('*')
        .eq('company_id', effectiveCompanyId)
        .maybeSingle()

      const { error: e2 } = await supabase
        .from('company_ciiu_activities')
        .select('ciiu_code, is_primary, ciiu_activities(risk_level)')
        .eq('company_id', effectiveCompanyId)

      if (e1 || e2) throw e1 || e2

      // Bloque 6: sin fila de perfil (p. ej. Florasíntesis) o con trabajadores o riesgo
      // en null, el motor no calcula lo que depende de ellos y arriba se pide completar
      // el Perfil SSO; ya no hay pantalla de error (decisión de JC, 23/10/2026).
      // Lote A: GT 14 (GT-20) aplica si la empresa registra productos químicos o bodegas.
      // Mantenimiento M1: GT 11 aplica con activos de ventilación; GT 27 y GT 28, con máquinas (inventario de activos).
      const activos = await conteoActivosChecklist(effectiveCompanyId)
      const profile: CompanyProfileForEngine = { ...perfilParaMotor(ssoProfile), tiene_quimicos_o_bodegas: await tieneQuimicosOBodegas(effectiveCompanyId),
        activos_ventilacion: activos?.ventilacion ?? null, activos_maquinas: activos?.maquinas ?? null,
        puestos_operan_vehiculos: await puestosOperanVehiculos(effectiveCompanyId).catch(() => null) }

      const generated = generateChecklist(profile)

      if (sincroniza) {
        // Lote E: PO-09, PO-10, PO-12 y PO-13 traen su cálculo (aviso al IESS y medidas) para mostrarlo como el plan.
        const trazasIncidentes = (await syncIncidentCompliance(effectiveCompanyId)) ?? {}
        await syncGa02Compliance(effectiveCompanyId)
        await syncWorkPermitCompliance(effectiveCompanyId)
        // Lote A: Inspecciones v2 firmadas → SP 1 a SP 9, GT 9 y GT 14, 21, 22, 26, 30 y 35 (insp-anexo1.ts). GT 9: mandan los
        // informes por puesto (lote D, con un informe de medidas aprobado: verificación in situ); si no, Inspecciones v2
        // firmada y vigente; si no, el módulo anterior (y la traza lo dice).
        const informesGT9 = await informesMandanGT9(effectiveCompanyId).catch(() => false)
        const anexo1 = await syncInspeccionesAnexo1(effectiveCompanyId, new Date(), { omitirGT9: informesGT9 }).catch(() => null)
        const estadoGT9Anterior = await syncInspectionCompliance(effectiveCompanyId, { omitirGT14: informesGT9 || !!anexo1?.v2MandaGT9 })
        const trazasAnexo1 = anexo1 && Object.keys(anexo1.trazas).length > 0
          ? { ...anexo1.trazas, ...(anexo1.v2MandaGT9 || informesGT9 ? {} : { 'GT-14': trazaGT9ModuloAnterior(estadoGT9Anterior) }) } : {}
        await syncProductsCompliance(effectiveCompanyId)
        await syncJobPositionsCompliance(effectiveCompanyId)
        // M61 (TH 5): licencias de conducción, solo si el motor dice que aplica.
        await syncLicenciasCompliance(effectiveCompanyId, !!generated.items.find(i => i.id === 'TH-05')?.aplica).catch(() => null)
        await syncIperCompliance(effectiveCompanyId)
        // F3a: PO-14, PO-15 y PO-23 desde el motor nuevo de Inspecciones (no se cruza con PO-16/17/18 del plan).
        const trazasPlanV2 = (await syncPlanEmergenciasCompliance(effectiveCompanyId)) ?? {}
        const trazasInspecciones = (await syncInspeccionesV2Compliance(effectiveCompanyId).catch(() => ({}))) ?? {}
        // M45: TH-06, TH-09, PO-04 y PO-22 desde los registros firmados por el trabajador.
        const trazasFirmas = (await syncFirmasCompliance(effectiveCompanyId).catch(() => ({}))) ?? {}
        // Lote C: TH-08, PO-02 y PO-24 a PO-27 desde los programas anuales (versión aprobada, ejecución y registro en el SUT).
        const trazasProgramas = (await syncProgramasCompliance(effectiveCompanyId).catch(() => ({}))) ?? {}
        // Lote D (M49): GT 5, 6 y 7 (y GT-15 a GT-17), GT 37 y TH 1, solo los ítems que aplican según el motor.
        const trazasLoteD = (await syncInformesPuesto(effectiveCompanyId, new Set(generated.items.filter(i => i.aplica).map(i => i.id))).catch(() => ({}))) ?? {}
        // Lote G (M51): GT 17 (GT-30) y señalización GT 31, 32, 33, 34 y 36 (mismos ids) desde las inspecciones firmadas; PO 21 desde la matriz y el procedimiento de EPP.
        const trazasLoteG = await syncLoteG(effectiveCompanyId, profile.transporta_quimicos_peligrosos).catch(() => ({}))
        const trazasPO21 = await syncEppPO21(effectiveCompanyId).catch(() => ({}))
        setTrazasPlan({ ...trazasPlanV2, ...trazasInspecciones, ...trazasFirmas, ...trazasProgramas, ...trazasLoteD, ...(trazasAnexo1 as unknown as TrazasPlanV2),
          ...(trazasLoteG as unknown as TrazasPlanV2), ...trazasPO21, ...(trazasIncidentes as unknown as TrazasPlanV2) })
      }

      const { data: estadosData } = await supabase
        .from('checklist_item_estados')
        .select('item_id, estado, auto_source, no_aplica_justificacion, no_aplica_por, no_aplica_at')
        .eq('company_id', effectiveCompanyId)

      const estadosMap: ItemEstado = {}
      const noAplicaData: Record<string, NoAplicaInfo> = {}
      generated.items.forEach(item => { estadosMap[item.id] = 'pendiente' })
      estadosData?.forEach((row: {
        item_id: string; estado: EstadoGuardado; auto_source: string | null
        no_aplica_justificacion: string | null; no_aplica_por: string | null; no_aplica_at: string | null
      }) => {
        estadosMap[row.item_id] = row.estado
        if (row.estado === 'no_aplica') {
          noAplicaData[row.item_id] = { justificacion: row.no_aplica_justificacion ?? '', por: row.no_aplica_por, at: row.no_aplica_at }
        }
      })
      setNoAplicaInfo(noAplicaData)
      leerNombres(Object.values(noAplicaData).map(n => n.por)).then(setNombres)

      const checklistConEstados: ComplianceChecklist = {
        ...generated,
        items: generated.items.map(item => ({
          ...item,
          estado: estadosMap[item.id] ?? 'pendiente',
        })),
      }

      setChecklist(checklistConEstados)
      setEstados(estadosMap)
      setScore(scoreChecklist(checklistConEstados))
      // F: todas las secciones empiezan cerradas, salvo que se entre con un ítem o un filtro.
      const e = entrada.current
      entrada.current = null
      const filtroInicial = e?.filtro && (FILTROS as readonly string[]).includes(e.filtro) ? e.filtro : null
      if (filtroInicial) setFiltro(filtroInicial)
      const destino = e?.item
        ? checklistConEstados.items.find(i => i.id === e.item)
        : filtroInicial && filtroInicial !== 'todos' ? primerItemDelFiltro(checklistConEstados.items, filtroInicial) : undefined
      if (destino && e?.item && filtroInicial && !coincideFiltro(destino, filtroInicial)) setFiltro('todos')
      setSeccionAbierta(destino ? grupoDeItem(destino) : null)
      if (destino && e?.item) setItemDestacado(destino.id)
    } catch (err: any) {
      setError('Error al cargar el checklist: ' + (err.message ?? 'Error desconocido'))
    } finally {
      setLoading(false)
    }
  }, [effectiveCompanyId, sincroniza])

  useEffect(() => { loadChecklist() }, [loadChecklist])

  const loadDocumentMap = useCallback(async () => {
    if (!effectiveCompanyId) return
    const { data, error: docErr } = await supabase
      .from('sso_documents')
      .select('id, checklist_item_id, file_url, created_at, approved_from, is_current')
      .eq('company_id', effectiveCompanyId)
      .not('checklist_item_id', 'is', null)
      .order('created_at', { ascending: false })
    if (docErr) { console.error('Error al cargar documentos vinculados:', docErr.message); return }
    const map = new Map<string, DocumentoVinculado[]>()
    data?.forEach((row: DocumentoVinculado & { checklist_item_id: string }) => {
      const existing = map.get(row.checklist_item_id) ?? []
      existing.push({ id: row.id, file_url: row.file_url, created_at: row.created_at, approved_from: row.approved_from, is_current: row.is_current })
      map.set(row.checklist_item_id, existing)
    })
    setDocumentMap(map)
  }, [effectiveCompanyId])

  useEffect(() => { loadDocumentMap() }, [loadDocumentMap])

  // El destino lo decide destinoResolver (src/lib/menu.ts, probado con todos los ítems):
  // GA y SP abren su página común (bloque 7, B4 y C4); el resto, su módulo como antes.
  const navegarA: Partial<Record<NavItem, (() => void) | undefined>> = {
    'sso-perfil': onNavigateToSSOPerfil,
    'vigilancia-salud': onNavigateToVigilanciaSalud,
    'permisos-trabajo': onNavigateToPermisos,
    'productos': onNavigateToProductos,
    'etiquetado-sga': onNavigateToEtiquetadoSGA,
    'inspecciones': onNavigateToInspecciones,
    'inspecciones-v2': onNavigateToInspeccionesV2,
    'puestos-trabajo': onNavigateToPuestos,
    'iper': onNavigateToMatrizRiesgos,
    'trabajadores': onNavigateToTrabajadores,
    'capacitaciones': onNavigateToCapacitaciones,
    'incidentes': onNavigateToIncidentes,
    'plan-emergencias': onNavigateToPlanEmergencias,
    'epp': onNavigateToEPP,
    'riesgo-psicosocial': onNavigateToRiesgoPsicosocial,
    'constructor-gestion-tecnica': onNavigateToConstructorGT,
  }

  const handleResolver = (itemId: string, modulo?: string) => {
    const destino = destinoResolver(itemId, modulo)
    if (!destino) return
    if (destino.tipo === 'item') { onNavigateToItemAnexo?.(itemId); return }
    if (destino.nav === 'documentos-sso') {
      // Sin documentos del ítem se abre directo el modal de subida con el ítem
      // elegido; con documentos, la lista filtrada (bloque 5b, G).
      sessionStorage.setItem('ss_init_checklist_item', itemId)
      if (!(documentMap.get(itemId)?.length)) sessionStorage.setItem('ss_init_subir', '1')
      onNavigateToDocumentosSSO?.()
      return
    }
    navegarA[destino.nav]?.()
  }

  // D: un mes de GA-07 sin informe abre Subir con GA-07 y "Aprobado desde" = día 1 de ese mes.
  const subirGA07 = (aprobadoDesde: string) => {
    sessionStorage.setItem('ss_init_checklist_item', 'GA-07')
    sessionStorage.setItem('ss_init_subir', '1')
    sessionStorage.setItem('ss_init_aprobado_desde', aprobadoDesde)
    onNavigateToDocumentosSSO?.()
  }

  // GA-11 por años (bloque 7, ítem 103): un año vacío abre Subir con el 1 de enero de ese año.
  const subirGA11 = (aprobadoDesde: string) => {
    sessionStorage.setItem('ss_init_checklist_item', 'GA-11')
    sessionStorage.setItem('ss_init_subir', '1')
    sessionStorage.setItem('ss_init_aprobado_desde', aprobadoDesde)
    onNavigateToDocumentosSSO?.()
  }

  // F: brecha crítica → se abre solo su sección y la vista va al ítem.
  const abrirItem = (item: ChecklistItem) => {
    if (!coincideFiltro(item, filtro)) setFiltro('todos')
    setSeccionAbierta(grupoDeItem(item))
    setItemDestacado(item.id)
  }

  const elegirFiltro = (f: string) => {
    setFiltro(f)
    if (f === 'todos' || !checklist) return
    const primero = primerItemDelFiltro(checklist.items, f)
    setSeccionAbierta(primero ? grupoDeItem(primero) : null)
  }

  const aplicarEstadoLocal = (itemId: string, nuevoEstado: EstadoKey) => {
    setEstados(prev => ({ ...prev, [itemId]: nuevoEstado }))
    setChecklist(prev => {
      if (!prev) return prev
      const updatedItems = prev.items.map(i => i.id === itemId ? { ...i, estado: nuevoEstado } : i)
      const updated = { ...prev, items: updatedItems }
      setScore(scoreChecklist(updated))
      return updated
    })
  }

  // PE-B2b: "Usar cálculo automático" en PO-16/17/18 (y en PO-14/15/23 de Inspecciones, F3a): quita el estado manual y
  // aplica lo que calcula su módulo.
  const usarCalculoAutomatico = async (itemId: string) => {
    const calculo = trazasPlan[itemId]
    if (!effectiveCompanyId || !calculo) return
    setSaving(itemId)
    setErrorEstado('')
    try {
      if (calculo.fuente === FUENTE_INCIDENTES_V2) {
        // Puede ser "No aplica" (con su justificación): se vuelve a cargar el Checklist entero.
        await aplicarCalculoIncidentes(effectiveCompanyId, itemId, calculo.estado as unknown as EstadoCalculoInc, calculo.resumen)
        await loadChecklist()
        return
      }
      if (calculo.fuente === FUENTE_INSPECCIONES_ANEXO1) {
        // Puede ser la sugerencia "No aplica" (con su justificación): se vuelve a cargar el Checklist entero.
        await aplicarCalculoAnexo1(effectiveCompanyId, itemId, calculo.estado as unknown as EstadoCalculoAnexo1, calculo.resumen)
        await loadChecklist()
        return
      }
      if (calculo.fuente === FUENTE_INSPECCIONES_V2) await aplicarCalculoInspecciones(effectiveCompanyId, itemId, calculo.estado)
      else if (calculo.fuente === FUENTE_FIRMAS) await aplicarCalculoFirmas(effectiveCompanyId, itemId, calculo.estado)
      else if (calculo.fuente === FUENTE_PROGRAMAS) await aplicarCalculoProgramas(effectiveCompanyId, itemId, calculo.estado)
      else if (calculo.fuente === FUENTE_INFORMES_PUESTO) await aplicarCalculoInformes(effectiveCompanyId, itemId, calculo.estado)
      else if (calculo.fuente === FUENTE_EPP_PO21) await aplicarCalculoEppPO21(effectiveCompanyId, calculo.estado)
      else await aplicarCalculoPlanV2(effectiveCompanyId, itemId, calculo.estado)
      aplicarEstadoLocal(itemId, calculo.estado)
      setTrazasPlan(prev => ({ ...prev, [itemId]: { ...prev[itemId], manualRespetado: false, estadoManual: null } }))
    } catch (err) {
      setErrorEstado(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(null)
    }
  }

  const updateEstado = async (itemId: string, nuevoEstado: EstadoKey) => {
    if (!effectiveCompanyId) return
    setSaving(itemId)
    setErrorEstado('')
    try {
      // E2: supabase-js no lanza el error, lo devuelve; antes se ignoraba y la
      // pantalla mostraba el estado aunque no se hubiera guardado.
      const { data: guardada, error: upErr } = await supabase.from('checklist_item_estados').upsert(
        { company_id: effectiveCompanyId, item_id: itemId, estado: nuevoEstado, auto_source: null, updated_at: new Date().toISOString() },
        { onConflict: 'company_id,item_id' }
      ).select('item_id')
      if (upErr || !guardada || guardada.length === 0) {
        console.error(`Error al guardar el estado de ${itemId}:`, upErr ?? 'sin filas (sin permiso)')
        setErrorEstado(`No se guardó "${ESTADO_CONFIG[nuevoEstado].label}" en ${itemId}${upErr?.code === '42501' ? ' (sin permiso para cambiar el checklist)' : ''}. Intenta de nuevo.`)
        return
      }
      aplicarEstadoLocal(itemId, nuevoEstado)
      // PE-B2b: en PO-16/17/18 el clic deja un estado manual; el plan ya no lo cambia.
      // Lote E: en PO-09/10/12/13 también se quita la etiqueta de "estado guardado" o "No aplica" del cálculo anterior.
      setTrazasPlan(prev => (prev[itemId] ? { ...prev, [itemId]: { ...prev[itemId], manualRespetado: true, estadoManual: nuevoEstado,
        ...({ etiquetaGuardado: undefined, notaGuardado: undefined } as object) } } : prev))
    } catch (err: any) {
      console.error(`Error inesperado al guardar el estado de ${itemId}:`, err)
      setErrorEstado(`No se guardó "${ESTADO_CONFIG[nuevoEstado].label}" en ${itemId}. Intenta de nuevo.`)
    } finally {
      setSaving(null)
    }
  }

  // ── C. "No aplica" manual (solo admin o superadmin; la base lo exige con M15) ──

  const aplicarLocal = (itemId: string, estado: EstadoGuardado, info: NoAplicaInfo | null) => {
    setEstados(prev => ({ ...prev, [itemId]: estado }))
    setNoAplicaInfo(prev => {
      const n = { ...prev }
      if (info) n[itemId] = info; else delete n[itemId]
      return n
    })
    setChecklist(prev => {
      if (!prev) return prev
      const updated = { ...prev, items: prev.items.map(i => i.id === itemId ? { ...i, estado } : i) }
      setScore(scoreChecklist(updated))
      return updated
    })
  }

  const abrirNoAplica = (itemId: string) => {
    setNoAplicaAbierto(itemId)
    setJustificacion('')
    setErrorJustificacion('')
  }

  const marcarNoAplica = async (item: ChecklistItem) => {
    const err = validarJustificacionNoAplica(justificacion)
    if (err) { setErrorJustificacion(err); return }
    if (!effectiveCompanyId || !puedeNoAplica) return
    setSaving(item.id)
    setErrorEstado('')
    try {
      const { data: userData } = await supabase.auth.getUser()
      const info: NoAplicaInfo = { justificacion: justificacion.trim(), por: userData.user?.id ?? null, at: new Date().toISOString() }
      const { data: guardada, error: upErr } = await supabase.from('checklist_item_estados').upsert(
        {
          company_id: effectiveCompanyId, item_id: item.id, estado: 'no_aplica', auto_source: null,
          no_aplica_justificacion: info.justificacion, no_aplica_por: info.por, no_aplica_at: info.at,
          updated_at: info.at,
        },
        { onConflict: 'company_id,item_id' }
      ).select('item_id')
      if (upErr || !guardada || guardada.length === 0) {
        console.error(`Error al marcar No aplica en ${item.id}:`, upErr ?? 'sin filas (sin permiso)')
        setErrorJustificacion(`No se guardó el "No aplica"${upErr?.code === '42501' ? ' (solo el administrador puede marcarlo)' : ''}. Intenta de nuevo.`)
        return
      }
      logAudit({
        company_id: effectiveCompanyId, action: 'updated', entity_type: 'checklist_item',
        entity_id: item.id, entity_name: `${item.id} — ${item.titulo}`,
        details: { accion: 'no_aplica_marcado', estado_anterior: estados[item.id] ?? 'pendiente', justificacion: info.justificacion },
      })
      if (info.por && perfilUsuario?.full_name) setNombres(prev => ({ ...prev, [info.por as string]: perfilUsuario.full_name as string }))
      aplicarLocal(item.id, 'no_aplica', info)
      setNoAplicaAbierto(null)
    } catch (e) {
      console.error(`Error inesperado al marcar No aplica en ${item.id}:`, e)
      setErrorJustificacion('No se guardó el "No aplica". Intenta de nuevo.')
    } finally {
      setSaving(null)
    }
  }

  // C6: deshacer vuelve a Pendiente (solo admin o superadmin).
  const deshacerNoAplica = async (item: ChecklistItem) => {
    if (!effectiveCompanyId || !puedeNoAplica) return
    const anterior = noAplicaInfo[item.id]
    setSaving(item.id)
    setErrorEstado('')
    try {
      const { data: guardada, error: upErr } = await supabase.from('checklist_item_estados').upsert(
        {
          company_id: effectiveCompanyId, item_id: item.id, estado: 'pendiente', auto_source: null,
          no_aplica_justificacion: null, no_aplica_por: null, no_aplica_at: null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'company_id,item_id' }
      ).select('item_id')
      if (upErr || !guardada || guardada.length === 0) {
        console.error(`Error al deshacer No aplica en ${item.id}:`, upErr ?? 'sin filas (sin permiso)')
        setErrorEstado(`No se pudo deshacer el "No aplica" de ${item.id}${upErr?.code === '42501' ? ' (solo el administrador puede hacerlo)' : ''}. Intenta de nuevo.`)
        return
      }
      logAudit({
        company_id: effectiveCompanyId, action: 'updated', entity_type: 'checklist_item',
        entity_id: item.id, entity_name: `${item.id} — ${item.titulo}`,
        details: { accion: 'no_aplica_deshecho', estado_nuevo: 'pendiente', justificacion_anterior: anterior?.justificacion ?? null },
      })
      aplicarLocal(item.id, 'pendiente', null)
    } catch (e) {
      console.error(`Error inesperado al deshacer No aplica en ${item.id}:`, e)
      setErrorEstado(`No se pudo deshacer el "No aplica" de ${item.id}. Intenta de nuevo.`)
    } finally {
      setSaving(null)
    }
  }

  if (loading) return (
    <div className="cl-loading">
      <div className="cl-spinner" />
      <p>Generando checklist personalizado…</p>
    </div>
  )

  if (error) return (
    <div className="cl-error">
      <span>⚠️</span>
      <p>{error}</p>
      <button onClick={loadChecklist} className="cl-btn-retry">Reintentar</button>
    </div>
  )

  if (!checklist) return null

  const secciones = Object.keys(SECCION_LABELS) as Array<keyof typeof SECCION_LABELS>
  const gaps = getCriticalGaps(checklist)
  const sinResponder = preguntasSinResponder(checklist.items).length

  const itemsFiltrados = (seccion: string) => {
    return checklist.items
      .filter(i => grupoDeItem(i) === seccion && coincideFiltro(i, filtro))
      .sort(compararPorNumeroLegal)
  }

  const sinItemsVisibles = secciones.every(seccion => itemsFiltrados(seccion).length === 0)

  const scoreColor = score >= 80 ? '#22c55e' : score >= 50 ? '#f59e0b' : '#ef4444'

  return (
    <div className="cl-page">

      {/* Header */}
      <div className="cl-header">
        <div className="cl-header-left">
          <h1 className="cl-title">Checklist Anexo 1 — MDT</h1>
          <p className="cl-subtitle">
            {checklist.total_aplicables} ítems aplicables · {checklist.total_no_aplica} no aplican · basado en tu perfil SSO
          </p>
        </div>
        <div className="cl-score-ring" style={{ '--score-color': scoreColor } as React.CSSProperties}>
          <span className="cl-score-num" style={{ color: scoreColor }}>{score}%</span>
          <span className="cl-score-label">cumplimiento</span>
        </div>
      </div>

      {/* Perfil incompleto (bloque 6): sin trabajadores o sin nivel de riesgo no se calculan las obligaciones. */}
      {checklist.perfil_incompleto && (
        <div className="cl-perfil-incompleto" role="status">
          <span>Completa tu Perfil SSO para calcular tus obligaciones (número de trabajadores y nivel de riesgo).</span>
          {onNavigateToSSOPerfil && (
            <button type="button" onClick={onNavigateToSSOPerfil}>Ir al Perfil SSO</button>
          )}
        </div>
      )}

      {/* Bloque 7: los "Sin calcular" no cuentan en el puntaje; se avisa si faltan respuestas del Perfil (M17). */}
      {sinResponder > 0 && (
        <div className="cl-perfil-incompleto" role="status" data-testid="aviso-preguntas-sin-responder">
          <span>{textoPreguntasSinResponder(sinResponder)}</span>
          {onNavigateToSSOPerfil && (
            <button type="button" onClick={onNavigateToSSOPerfil}>Ir al Perfil SSO</button>
          )}
        </div>
      )}

      {/* Alertas críticas */}
      {gaps.length > 0 && (
        <div className="cl-gaps">
          <p className="cl-gaps-title">⚠️ Brechas críticas sin resolver</p>
          <div className="cl-gaps-list">
            {gaps.map(g => (
              <button key={g.id} type="button" className="cl-gap-item" onClick={() => abrirItem(g)}
                aria-label={`Ir a ${etiquetaLegal(g)}: ${g.titulo}`}>
                <span className="cl-gap-id" title={`Id interno: ${g.id}`}>{etiquetaLegal(g)}</span>
                <span className="cl-gap-titulo">{g.titulo}</span>
                <span className="cl-gap-peso">{g.peso.toFixed(1)}%</span>
                <span className="cl-gap-flecha" aria-hidden="true">›</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {errorEstado && (
        <div role="alert" className="cl-error-estado">
          <span>{errorEstado}</span>
          <button type="button" onClick={() => setErrorEstado('')} aria-label="Cerrar aviso">✕</button>
        </div>
      )}

      {/* Filtros */}
      <div className="cl-filtros">
        {FILTROS.map(f => (
          <button
            key={f}
            className={`cl-filtro-btn ${filtro === f ? 'cl-filtro-activo' : ''}`}
            aria-pressed={filtro === f}
            onClick={() => elegirFiltro(f)}
          >
            {f === 'todos' ? 'Todos' :
             f === 'aplica' ? 'Aplicables' :
             f === 'no_aplica' ? 'No aplica' :
             ESTADO_CONFIG[f as EstadoKey]?.label ?? f}
          </button>
        ))}
      </div>

      {/* Secciones */}
      {secciones.map(seccion => {
        const items = itemsFiltrados(seccion)
        if (items.length === 0) return null
        const abierta = seccionAbierta === seccion

        return (
          <div key={seccion} className="cl-seccion">
            <button
              className="cl-seccion-header"
              aria-expanded={abierta}
              onClick={() => setSeccionAbierta(abierta ? null : seccion)}
            >
              <span className="cl-seccion-label">{SECCION_LABELS[seccion]}</span>
              <div className="cl-seccion-meta">
                <span className="cl-seccion-count">
                  {checklist.items.filter(i => grupoDeItem(i) === seccion && i.aplica && i.estado === 'cumple').length}
                  /
                  {checklist.items.filter(i => grupoDeItem(i) === seccion && i.aplica && i.estado !== 'no_aplica').length} cumplidos
                </span>
                <span className={`cl-chevron ${abierta ? 'cl-chevron-open' : ''}`}>›</span>
              </div>
            </button>

            {abierta && (
              <div className="cl-items">
                {seccion === 'complementarias' && (
                  <p className="cl-complementarias-nota">
                    Obligaciones vigentes que no son ítems del Anexo 1 (o lo desglosan). Cuentan en el cumplimiento igual que antes.
                  </p>
                )}
                {items.map(item => (
                  <div key={item.id} id={`cl-item-${item.id}`}
                    className={`cl-item ${!item.aplica ? 'cl-item-na' : ''} ${itemDestacado === item.id ? 'cl-item-destacado' : ''}`}>
                    <div className="cl-item-top">
                      <span className="cl-item-id">{etiquetaLegal(item)}</span>
                      <span className="cl-item-titulo">{item.titulo}</span>
                      {item.numero_legal != null && (
                        <span className="cl-item-id-interno" title="Id interno del ítem en StorSafe">{item.id}</span>
                      )}
                      {item.categoria_gt && (
                        <span className="cl-item-categoria">{CATEGORIA_GT_LABELS[item.categoria_gt]}</span>
                      )}
                      {item.aplica && (
                        <span className="cl-item-peso">{item.peso.toFixed(1)}%</span>
                      )}
                    </div>

                    <p className="cl-item-desc">{item.descripcion}</p>

                    {item.normativa.length > 0 && (
                      <div className="cl-item-normativa">
                        {item.normativa.map((n, i) => (
                          <span key={i} className="cl-norm-badge">{n}</span>
                        ))}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                      <button
                        type="button"
                        title="Resolver"
                        disabled={!moduloTieneNavegacion(item.id, item.modulo_storsafe)}
                        onClick={moduloTieneNavegacion(item.id, item.modulo_storsafe) ? () => handleResolver(item.id, item.modulo_storsafe) : undefined}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 28, height: 28, borderRadius: 6, border: '1px solid #334155',
                          background: 'transparent',
                          color: moduloTieneNavegacion(item.id, item.modulo_storsafe) ? '#94a3b8' : '#334155',
                          cursor: moduloTieneNavegacion(item.id, item.modulo_storsafe) ? 'pointer' : 'not-allowed',
                          opacity: moduloTieneNavegacion(item.id, item.modulo_storsafe) ? 1 : 0.4,
                        }}
                      >
                        <IconResolver />
                      </button>
                      {/* GA-07 (5d, D): la fila de meses reemplaza a los íconos iguales de PDF. */}
                      {item.id !== 'GA-07' && item.id !== 'GA-11' && (documentMap.get(item.id) ?? []).map((doc, i, arr) => (
                        <button
                          key={doc.file_url}
                          type="button"
                          title={arr.length > 1 ? `Ver PDF ${i + 1} de ${arr.length} (${new Date(doc.created_at).toLocaleDateString('es-EC')})` : 'Ver PDF'}
                          onClick={() => void abrirArchivo('sso-documents', doc.file_url)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 28, height: 28, borderRadius: 6, border: '1px solid #334155',
                            background: 'transparent', color: '#94a3b8', cursor: 'pointer',
                          }}
                        >
                          <IconPDF />
                        </button>
                      ))}
                    </div>

                    {item.id === 'GA-07' && (
                      <MesesGA07
                        informes={documentMap.get('GA-07') ?? []}
                        onAbrir={inf => void abrirArchivo('sso-documents', inf.file_url)}
                        onSubir={onNavigateToDocumentosSSO ? subirGA07 : undefined}
                      />
                    )}
                    {item.id === 'GA-11' && (
                      <AniosGA11
                        informes={documentMap.get('GA-11') ?? []}
                        onAbrir={inf => void abrirArchivo('sso-documents', inf.file_url)}
                        onSubir={onNavigateToDocumentosSSO ? subirGA11 : undefined}
                      />
                    )}

                    {!item.aplica ? (
                      // C5: el N/A automático del perfil no se cambia a mano.
                      <p className="cl-item-na-razon">
                        {item.sin_calcular ? item.razon_no_aplica : `N/A — ${item.razon_no_aplica}`}
                      </p>
                    ) : estados[item.id] === 'no_aplica' ? (
                      <div className="cl-no-aplica-manual">
                        <p>
                          {textoNoAplicaManual(
                            noAplicaInfo[item.id]?.por ? (nombres[noAplicaInfo[item.id].por as string] ?? null) : null,
                            noAplicaInfo[item.id]?.at ?? null,
                            noAplicaInfo[item.id]?.justificacion ?? '',
                          )}
                        </p>
                        {puedeNoAplica ? (
                          <button type="button" className="cl-btn-secundario" disabled={saving === item.id}
                            onClick={() => deshacerNoAplica(item)}>
                            {saving === item.id ? 'Guardando…' : 'Deshacer (vuelve a Pendiente)'}
                          </button>
                        ) : (
                          <span className="cl-nota-rol">Solo el administrador puede deshacerlo.</span>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="cl-item-estados">
                          {(() => {
                            // D1: en los ítems automáticos manda el módulo; los botones quedan bloqueados.
                            const bloqueado = esAutomatico(item.id)
                            return (Object.keys(ESTADO_CONFIG) as EstadoKey[]).map(est => (
                              <button
                                key={est}
                                disabled={saving === item.id || bloqueado || !puedeEditar}
                                aria-pressed={estados[item.id] === est}
                                title={bloqueado ? `Lo decide ${ITEMS_AUTOMATICOS[item.id].modulo}` : !puedeEditar ? 'Solo el administrador o el responsable pueden cambiar el estado' : undefined}
                                className={`cl-estado-btn ${ESTADO_CONFIG[est].cls} ${estados[item.id] === est ? 'cl-estado-activo' : ''}`}
                                onClick={() => updateEstado(item.id, est)}
                              >
                                {saving === item.id && estados[item.id] !== est ? '…' : ESTADO_CONFIG[est].label}
                              </button>
                            ))
                          })()}
                          {muestraBotonNoAplica(item) && (
                            <button
                              type="button"
                              className="cl-estado-btn cl-estado-noaplica"
                              disabled={!puedeNoAplica || saving === item.id}
                              title={puedeNoAplica ? 'Marcar como No aplica con justificación' : 'Solo el administrador de la empresa puede marcar No aplica'}
                              onClick={() => abrirNoAplica(item.id)}
                            >
                              No aplica
                            </button>
                          )}
                          {esAutomatico(item.id) && (
                            <span className="cl-norm-badge" title="Este ítem lo marca su módulo y no admite edición manual">
                              🔒 Automático
                            </span>
                          )}
                        </div>
                        {esAutomatico(item.id) && (
                          <p className="cl-regla-automatica">
                            Lo decide {ITEMS_AUTOMATICOS[item.id].modulo}: {ITEMS_AUTOMATICOS[item.id].regla}. {REGLA_COMUN_AUTOMATICOS}{' '}
                            {moduloTieneNavegacion(item.id, ITEMS_AUTOMATICOS[item.id].modulo) && (
                              <button type="button" className="cl-enlace"
                                onClick={() => handleResolver(item.id, ITEMS_AUTOMATICOS[item.id].modulo)}>
                                {abrePaginaDelItem(item.id) && onNavigateToItemAnexo ? 'Ir a la página del ítem' : `Ir a ${ITEMS_AUTOMATICOS[item.id].modulo}`}
                              </button>
                            )}
                          </p>
                        )}
                        {trazasPlan[item.id] && (
                          <div className="cl-regla-automatica" data-testid={`traza-plan-${item.id}`}>
                            {(trazasPlan[item.id] as unknown as TrazaItemIncidentes).advertencia && (
                              <p role="alert" data-testid={`advertencia-${item.id}`} style={{ border: '2px solid #dc2626', background: 'rgba(220,38,38,0.15)', color: '#fecaca', borderRadius: 8, padding: '8px 10px', fontWeight: 700 }}>
                                ⚠ {(trazasPlan[item.id] as unknown as TrazaItemIncidentes).advertencia}
                              </p>
                            )}
                            {trazasPlan[item.id].manualRespetado ? (
                              <p>
                                <b>{(trazasPlan[item.id] as unknown as TrazaItemIncidentes).etiquetaGuardado ?? 'Estado manual'} · el cálculo automático dice: {etiquetaCalculo(trazasPlan[item.id].estado)}.</b>{' '}
                                {trazasPlan[item.id].resumen} {(trazasPlan[item.id] as unknown as TrazaItemIncidentes).notaGuardado ?? `${trazasPlan[item.id].modulo ?? 'El Plan de Emergencias'} no cambia un estado marcado a mano.`}{' '}
                                {puedeEditar && (
                                  <button type="button" className="cl-enlace" disabled={saving === item.id} onClick={() => usarCalculoAutomatico(item.id)}>
                                    Usar cálculo automático
                                  </button>
                                )}
                              </p>
                            ) : (
                              <p>
                                Lo calcula {trazasPlan[item.id].modulo ?? 'el Plan de Emergencias'}: {etiquetaCalculo(trazasPlan[item.id].estado)}. {trazasPlan[item.id].resumen}
                              </p>
                            )}
                            <ul>
                              {trazasPlan[item.id].trazas.map((t, i) => (
                                <li key={i}>{t.norma} · {t.articulo_o_tabla} — {t.estado === 'por_validar' ? 'por validar' : t.estado.replace('_', ' ')}. Fuente: {t.fuente_doc}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {muestraBotonNoAplica(item) && !puedeNoAplica && (
                          <p className="cl-nota-rol">"No aplica" lo marca solo el administrador de la empresa.</p>
                        )}
                        {noAplicaAbierto === item.id && (
                          <div className="cl-no-aplica-form">
                            <label htmlFor={`na-${item.id}`}>
                              ¿Por qué no aplica? (mínimo {MIN_JUSTIFICACION_NO_APLICA} caracteres; queda en la auditoría)
                            </label>
                            <textarea
                              id={`na-${item.id}`}
                              value={justificacion}
                              onChange={e => { setJustificacion(e.target.value); setErrorJustificacion('') }}
                              rows={3}
                            />
                            {errorJustificacion && <p role="alert" className="cl-error-campo">{errorJustificacion}</p>}
                            <div className="cl-no-aplica-acciones">
                              <button type="button" className="cl-btn-secundario" onClick={() => setNoAplicaAbierto(null)} disabled={saving === item.id}>
                                Cancelar
                              </button>
                              <button type="button" className="cl-btn-primario" onClick={() => marcarNoAplica(item)} disabled={saving === item.id}>
                                {saving === item.id ? 'Guardando…' : 'Marcar No aplica'}
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}

      {sinItemsVisibles && (
        <div style={{ padding: 48, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>
          Ningún ítem coincide con el filtro seleccionado. Prueba con otro filtro.
        </div>
      )}

      {/* C8: informe del Checklist. StorSafe no tiene un "plan integral"; la justificación queda aquí y en audit_logs. */}
      {checklist.items.some(i => i.aplica && estados[i.id] === 'no_aplica') && (
        <section className="cl-informe-no-aplica" aria-labelledby="cl-informe-na-titulo">
          <h2 id="cl-informe-na-titulo">Ítems marcados como No aplica</h2>
          <p>No suman ni restan en el cumplimiento. Cada marca queda en la auditoría con su justificación.</p>
          <ul>
            {checklist.items.filter(i => i.aplica && estados[i.id] === 'no_aplica').sort(compararPorNumeroLegal).map(i => (
              <li key={i.id}>
                <strong>{etiquetaLegal(i)} — {i.titulo}</strong>
                <span>
                  {textoNoAplicaManual(
                    noAplicaInfo[i.id]?.por ? (nombres[noAplicaInfo[i.id].por as string] ?? null) : null,
                    noAplicaInfo[i.id]?.at ?? null,
                    noAplicaInfo[i.id]?.justificacion ?? '',
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

function IconResolver() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  )
}

function IconPDF() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
    </svg>
  )
}
